/** Marketing line used on the home hero, footers, and similar surfaces. */
export const ASSEMBLY_TAGLINE =
  "Connecting pastors and ministry leaders worldwide";

/**
 * True when this session account may use gated platform surfaces.
 * Organizations and individuals both use status === "active".
 *
 * @param {Object|null|undefined} user
 * @returns {boolean}
 */
export function isAccountVerified(user) {
  if (!user) return false;
  if (typeof user.status === "string" && user.status.length > 0) {
    return user.status === "active";
  }
  if (user.type === "organization") return false;
  return user.verified === true;
}

/**
 * True when this person is looking at their own profile and still waiting for review.
 * Rejected accounts are not described as waiting.
 *
 * @param {Object|null|undefined} user
 * @param {boolean} isOwnProfile
 * @returns {boolean}
 */
export function showVerificationWaitNotice(user, isOwnProfile) {
  if (!isOwnProfile || !user || user.type === "organization") return false;
  if (user.status === "rejected") return false;
  return !isAccountVerified(user);
}

/**
 * Assembly marketing social profiles and shared brand icon assets.
 * `hosts` lists domains that should resolve to the same icon (profile links, etc.).
 * @type {ReadonlyArray<{ label: string, href: string, iconSrc: string, hosts: string[] }>}
 */
export const SOCIAL_LINKS = [
  {
    label: "Instagram",
    href: "https://instagram.com/assemblyplatform",
    iconSrc: "/icons/instagram_icon.png",
    hosts: ["instagram.com", "www.instagram.com"],
  },
  {
    label: "YouTube",
    href: "https://youtube.com/@assemblyplatform",
    iconSrc: "/icons/youtube_icon.png",
    hosts: ["youtube.com", "www.youtube.com", "m.youtube.com", "youtu.be"],
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/assembly-platform",
    iconSrc: "/icons/linkedin_icon.webp",
    hosts: ["linkedin.com", "www.linkedin.com"],
  },
];

const SOCIAL_ICON_BY_HOST = Object.fromEntries(
  SOCIAL_LINKS.flatMap(({ iconSrc, hosts }) =>
    hosts.map((host) => [host, iconSrc])
  )
);

/**
 * Prefer local brand icons for known social hosts; otherwise use fallback.
 * @param {string} url
 * @param {string} [fallbackIconUrl]
 * @returns {string}
 */
export function resolveSocialIconSrc(url, fallbackIconUrl = "") {
  try {
    const host = new URL(url).hostname.toLowerCase();
    if (SOCIAL_ICON_BY_HOST[host]) return SOCIAL_ICON_BY_HOST[host];
  } catch {
    /* ignore invalid URLs — fall through */
  }
  return fallbackIconUrl || "";
}

/**
 * Format a timestamp as a short relative age (`1m`, `8h`, `3w`), or a
 * calendar date (`8/8/2026`) when older than 10 weeks. Floors each unit;
 * minimum display is `1m`.
 *
 * @param {string|number|Date} [timestamp]
 * @param {Date} [now]
 * @returns {string}
 */
export function formatRelativeTimestamp(timestamp, now = new Date()) {
  if (timestamp == null || timestamp === "") return "";
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";

  const diffMs = Math.max(0, now.getTime() - date.getTime());
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;
  const weekMs = 7 * dayMs;

  const weeks = Math.floor(diffMs / weekMs);
  if (weeks > 10) {
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  }
  if (weeks >= 1) {
    return `${weeks}w`;
  }

  const days = Math.floor(diffMs / dayMs);
  if (days >= 1) {
    return `${days}d`;
  }

  const hours = Math.floor(diffMs / hourMs);
  if (hours >= 1) {
    return `${hours}h`;
  }

  const minutes = Math.floor(diffMs / minuteMs);
  if (minutes >= 1) {
    return `${minutes}m`;
  }

  return `now`; // Less than 1 minute ago.
}

/** Milliseconds within which last_online counts as "active now". */
export const PRESENCE_ONLINE_MS = 30 * 1000;

/** How often presence labels and DM dots should re-evaluate. */
export const PRESENCE_TICK_MS = 5 * 1000;

/**
 * Epoch ms for a last_online stamp, or 0 when missing/invalid.
 *
 * @param {string|Date|null|undefined} lastOnline
 * @returns {number}
 */
export function presenceStampMs(lastOnline) {
  if (lastOnline == null || lastOnline === "") return 0;
  const date = lastOnline instanceof Date ? lastOnline : new Date(lastOnline);
  const value = date.getTime();
  return Number.isNaN(value) ? 0 : value;
}

/**
 * Whether a last_online timestamp is within the active-now window.
 *
 * @param {string|Date|null|undefined} lastOnline
 * @param {Date} [now]
 * @returns {boolean}
 */
export function isUserOnline(lastOnline, now = new Date()) {
  if (lastOnline == null || lastOnline === "") return false;
  const date = lastOnline instanceof Date ? lastOnline : new Date(lastOnline);
  if (Number.isNaN(date.getTime())) return false;
  return now.getTime() - date.getTime() < PRESENCE_ONLINE_MS;
}

/**
 * Human-readable last-active label for DMs and profiles.
 *
 * @param {string|Date|null|undefined} lastOnline
 * @param {Date} [now]
 * @returns {string}
 */
export function formatLastActive(lastOnline, now = new Date()) {
  if (lastOnline == null || lastOnline === "") return "Offline";
  const date = lastOnline instanceof Date ? lastOnline : new Date(lastOnline);
  if (Number.isNaN(date.getTime())) return "Offline";

  const diffMs = Math.max(0, now.getTime() - date.getTime());
  if (diffMs < PRESENCE_ONLINE_MS) return "Active now";
  if (diffMs < 60 * 1000) return "Active 30s ago";

  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;
  const minutes = Math.floor(diffMs / minuteMs);
  if (minutes < 60) {
    return `Active ${minutes}m ago`;
  }
  const hours = Math.floor(diffMs / hourMs);
  if (hours < 24) {
    return `Active ${hours}h ago`;
  }
  const days = Math.floor(diffMs / dayMs);
  if (days < 7) {
    return `Active ${days}d ago`;
  }
  return `Active ${date.toLocaleDateString()}`;
}

/**
 * Shorten a geocoded address for compact UI (e.g. feed metadata).
 * Prefers "City, State" when the stored value includes a street line.
 *
 * @param {string} [location]
 * @returns {string}
 */
export function formatShortLocation(location) {
  if (!location || typeof location !== "string") return "";
  const parts = location
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length >= 3) {
    return `${parts[1]}, ${parts[2]}`;
  }
  return location.trim();
}

/**
 * Compact "role · org" line for a feed author.
 *
 * @param {{ author_role?: string, author_org_name?: string }|null|undefined} item
 * @returns {string}
 */
export function formatAuthorAffiliation(item) {
  const role = (item?.author_role || "").trim();
  const org = (item?.author_org_name || "").trim();
  if (role && org) return `${role} · ${org}`;
  if (role) return role;
  if (org) return org;
  return "";
}

/**
 * Explore URL that centers the map on a temporary pin.
 *
 * @param {number|string} latitude
 * @param {number|string} longitude
 * @param {string} [label]
 * @returns {string}
 */
export function buildExploreLocationPath(latitude, longitude, label = "") {
  const lat = Number(latitude);
  const lon = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || (lat === 0 && lon === 0)) {
    return "/explore";
  }
  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lon),
  });
  const trimmedLabel = typeof label === "string" ? label.trim() : "";
  if (trimmedLabel) {
    params.set("label", trimmedLabel);
  }
  return `/explore?${params.toString()}`;
}

/** Served from `frontend/public/defaults/` (same-origin). */
export const defaultPfpUrl = "/defaults/default_pfp.jpg";
export const defaultGroupPfpUrl = "/defaults/default_group_pfp.jpg";

/** Themes shown on the main Appearance page and in the header picker. */
export const standardThemes = [
    { id: "light", label: "Light" },
    { id: "midnight", label: "Dark" },
];

/** Extra palettes behind Appearance → Special themes (beta). */
export const specialThemes = [
    { id: "dark", label: "Charcoal" }, // or 'Graphite', 'Carbon', 'Slate'
    { id: "forest", label: "Forest" },
    { id: "ocean", label: "Ocean" },
    { id: "sunset", label: "Sunset" },
    { id: "peach", label: "Peach" },
    { id: "cream", label: "Cream" },
    { id: "crimson", label: "Crimson" },
    { id: "strawberry", label: "Strawberry" },
    { id: "high-contrast", label: "High Contrast" },
];

/** All selectable palettes (standard + special). */
export const availableThemes = [...standardThemes, ...specialThemes];

/** CSS class ids for {@link availableThemes}. */
export const availableThemeIds = availableThemes.map((theme) => theme.id);

const specialThemeIds = new Set(specialThemes.map((theme) => theme.id));

/**
 * Whether this stored preference is a special (beta) theme.
 * @param {string} themeId
 * @returns {boolean}
 */
export function isSpecialTheme(themeId) {
    return specialThemeIds.has(themeId);
}

/** Use OS light/dark preference when the user selects this option. */
export const THEME_SYSTEM = "system";

/** Theme `id` used when System follows a light OS preference. */
export const SYSTEM_LIGHT_THEME_ID = "light";

/** Theme `id` used when System follows a dark OS preference. */
export const SYSTEM_DARK_THEME_ID = "midnight";

/**
 * Display name for a theme id, used on the settings page and theme dropdown.
 *
 * @param {string} themeId - A theme id or {@link THEME_SYSTEM}.
 * @returns {string} Human-readable label.
 */
export function getThemeLabel(themeId) {
  if (themeId === THEME_SYSTEM) {
    return "System";
  }
  return availableThemes.find((theme) => theme.id === themeId)?.label ?? themeId;
}

/**
 * Returns the concrete theme class string applied to `document.documentElement`.
 * System preference follows `prefers-color-scheme`
 * ({@link SYSTEM_DARK_THEME_ID} vs {@link SYSTEM_LIGHT_THEME_ID}).
 *
 * @param {string} themePreference - Stored selection: a theme id or {@link THEME_SYSTEM}.
 * @returns {string} One of {@link availableThemeIds}.
 */
export function resolveAppliedTheme(themePreference) {
  const lightId = availableThemeIds.includes(SYSTEM_LIGHT_THEME_ID)
    ? SYSTEM_LIGHT_THEME_ID
    : "light";
  const darkId = availableThemeIds.includes(SYSTEM_DARK_THEME_ID)
    ? SYSTEM_DARK_THEME_ID
    : lightId;

  if (themePreference === THEME_SYSTEM) {
    if (typeof window === "undefined") {
      return lightId;
    }
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? darkId
      : lightId;
  }
  if (availableThemeIds.includes(themePreference)) {
    return themePreference;
  }
  return lightId;
}

/**
 * Whether the resolved theme should show the sun icon (the System light theme).
 *
 * @param {string} resolvedTheme - Output of {@link resolveAppliedTheme}.
 * @returns {boolean}
 */
export function isLightThemeClass(resolvedTheme) {
  return resolvedTheme === SYSTEM_LIGHT_THEME_ID;
}

export const affiliationRoles = [
    'Lead Pastor',
    'Associate Pastor',
    'Assistant Pastor',
    'Worship Pastor',
    'Worship Director',
    'Community Pastor',
    'Children\'s Pastor',
    'Youth Pastor',
    'Young Adults Pastor',
    'Women\'s Pastor',
    'Men\'s Pastor',
    'Legacy Pastor',
    'Worship Leader',
    'Music Director',
    'Technician',
    'Sound Engineer',
    'Video Director',
    'Videographer',
    'Facility Supervisor',
    'Technical Director',
    'Lifegroups Pastor',
    'Connections Pastor',
    'Discipleship Pastor',
    'Executive Pastor',
    'Administrator',
    'Elder',
    'Deacon',
    'Deaconess',
    'Ministry Leader',
    'Small Group Leader',
    'Volunteer Coordinator',
    'Board Member',
    'Trustee',
    'Treasurer',
    'Secretary',
    'Outreach Director',
    'Hospitality Team Lead',
    'Greeter',
    'Usher',
    'Prayer Team Leader',
    'Prayer Coordinator',
    'Missions Pastor',
    'Campus Pastor',
    'Guest Pastor',
    'Chaplain',
    'Sunday School Teacher',
    'Children\'s Director',
    'Nursery Coordinator',
    'Student Ministries Director',
    'Media Director',
    'Communications Director',
    'Facilities Manager',
    'Security Team Lead',
    'Administrative Assistant',
]

export const denominations = [
    // Major branches
    'Anglican',
    'Baptist',
    'Catholic',
    'Eastern Orthodox',
    'Oriental Orthodox',
    'Assyrian Church of the East',
    'Lutheran',
    'Methodist',
    'Presbyterian',
    'Reformed',
    'Pentecostal',
    'Anabaptist',
    'Adventist',
    'Quaker (Religious Society of Friends)',
    'Mennonite',
    'Amish',
    'Moravian',
    'Congregationalist',
    'Restoration Movement',
    'Church of Christ',
    'Disciples of Christ',
    'Christian Church (Independent)',
    'United Church of Christ',
    'Non-denominational',
    'Evangelical',
    'Charismatic',
    'Holiness',
    'Salvation Army',
    // 'Jehovah\'s Witnesses',
    // 'Latter-day Saints (Mormon)',
    // 'Unitarian',
    'United Protestant',
    'Messianic Judaism',

    // Eastern/Oriental Traditions
    'Greek Orthodox',
    'Russian Orthodox',
    'Coptic Orthodox',
    'Syriac Orthodox',
    'Ethiopian Orthodox',
    'Armenian Apostolic',
    'Malankara Orthodox Syrian',
    'Ukrainian Orthodox',

    // Catholic Rites (in communion with Rome)
    'Roman Catholic (Latin Rite)',
    'Byzantine Catholic',
    'Maronite Catholic',
    'Melkite Greek Catholic',
    'Syro-Malabar Catholic',
    'Syro-Malankara Catholic',
    'Chaldean Catholic',
    'Armenian Catholic',
    'Coptic Catholic',
    'Ethiopian Catholic',
    'Ukrainian Greek Catholic',
    'Ruthenian Catholic',

    // Baptist Subgroups
    'Southern Baptist',
    'National Baptist',
    'American Baptist',
    'Primitive Baptist',
    'Missionary Baptist',
    'Cooperative Baptist Fellowship',
    'Northern Baptist',
    'Free Will Baptist',
    'Regular Baptist',

    // Methodist Subgroups
    'United Methodist',
    'African Methodist Episcopal',
    'African Methodist Episcopal Zion',
    'Free Methodist',
    'Wesleyan',
    'Nazarene',

    // Presbyterian Subgroups
    'Presbyterian Church (USA)',
    'Presbyterian Church in America',
    'Orthodox Presbyterian Church',
    'Cumberland Presbyterian',
    'Evangelical Presbyterian Church',

    // Pentecostal Subgroups
    'Assemblies of God',
    'Church of God (Cleveland, TN)',
    'Church of God in Christ',
    'Pentecostal Holiness',
    'Foursquare Gospel',
    'United Pentecostal Church',

    // Adventist Subgroups
    'Seventh-day Adventist',
    'Church of God (Seventh-Day)',
    'Advent Christian Church',

    // Anabaptist Subgroups
    'Brethren',
    'Schwarzenau Brethren',
    'Hutterite',

    // Other Protestant/Freemovement
    'Episcopal',
    'United Church of Canada',
    'Free Church',
    'Swedenborgian',
    'Unity Church',
    'Friends United Meeting',
    'Church of the Brethren',
    'Church of Scotland',

    // Mega/World Denominations
    'Redeemed Christian Church of God',

    // Catch-all and custom identities
    'Other',
    'Unknown/Do Not Wish to State'
];

export const staffMembers = [
    {
        name: 'Sam Gibson',
        role: 'Software & Technology',
        bio: 'Leading technical development and platform architecture to create a seamless networking experience for pastors and church leaders.',
        imageUrl: '/headshots/sam.jpg',
        linkedInUrl: 'https://www.assembly-platform.com/profile/3' // 'https://www.linkedin.com/in/samuel-gibson-816b52268/'
    },
    {
        name: 'Bernard Legaspi',
        role: 'Vision & Strategy',
        bio: 'Driving the strategic direction and long-term vision for Assembly, ensuring we serve the unique needs of pastoral communities.',
        imageUrl: '/headshots/bernard.jpg',
        linkedInUrl: 'https://www.assembly-platform.com/profile/4' // 'https://www.linkedin.com/in/bernard-legaspi/'
    },
    {
        name: 'Jeff Wang',
        role: 'Communication & Outreach',
        bio: 'Building bridges between pastors and churches, fostering meaningful connections and community engagement across the body of Christ.',
        imageUrl: '/headshots/jeff.jpeg',
        linkedInUrl: 'https://www.assembly-platform.com/profile/5' // 'https://www.linkedin.com/in/jwang343/'
    }
];